'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';

interface SSEEvent {
  type: string;
  [key: string]: unknown;
}

interface UseSqlQueryAnalyzeReturn {
  markdown: string;
  isStreaming: boolean;
  isComplete: boolean;
  error: string | null;
  analyze: (sqlQueryExecutionIds: string[]) => void;
  reset: () => void;
}

export function useSqlQueryAnalyze(
  workspaceId: string
): UseSqlQueryAnalyzeReturn {
  const { clientApiBaseUrl, authToken } = useEnvironment();
  const { currentProjectId } = useUser();

  const [markdown, setMarkdown] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMarkdown('');
    setIsStreaming(false);
    setIsComplete(false);
    setError(null);
  }, []);

  const analyze = useCallback(
    (sqlQueryExecutionIds: string[]) => {
      if (isStreaming) return;

      reset();
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const url = `${clientApiBaseUrl}/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/sql-query/analyze`;

      fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ sqlQueryExecutionIds }),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(text || `HTTP ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('No response body');
          }

          const decoder = new TextDecoder();
          let buffer = '';

          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const chunks = buffer.split('\n\n');
            buffer = chunks.pop() ?? '';

            for (const chunk of chunks) {
              const trimmed = chunk.trim();
              if (!trimmed) continue;

              if (trimmed === 'data: [DONE]') {
                return;
              }

              if (trimmed.startsWith('data: ')) {
                try {
                  const event: SSEEvent = JSON.parse(trimmed.slice(6));
                  processEvent(event);
                } catch {
                  // Ignore malformed JSON
                }
              }
            }
          }
        })
        .catch((err: Error) => {
          if (err.name === 'AbortError') return;
          setError(err.message || 'Failed to generate analysis');
        })
        .finally(() => {
          setIsStreaming(false);
          setIsComplete(true);
        });

      function processEvent(event: SSEEvent) {
        switch (event.type) {
          case 'text-delta':
            setMarkdown((prev) => prev + (event.delta as string));
            break;

          case 'error':
            setError((event.errorText as string) || 'An error occurred');
            break;
        }
      }
    },
    [
      isStreaming,
      reset,
      clientApiBaseUrl,
      currentProjectId,
      workspaceId,
      authToken,
    ]
  );

  // Abort on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return { markdown, isStreaming, isComplete, error, analyze, reset };
}
